// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type {
  DocumentReference,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { ASSESSMENT_CATALOG } from '../constants/medical-info';
import {
  createVersionedQuestionnaireDocument,
  getQuestionnaireDocumentSource,
  QUESTIONNAIRE_DOCUMENT_TYPE_CODE,
  QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM,
} from '../utils/questionnaire-document';

// linkId → display name for triage intake uploads stored in QuestionnaireResponse items
const INTAKE_UPLOAD_LINK_IDS: Record<string, string> = {
  'stopbang-upload': 'STOP-BANG',
  'ess-upload': 'Epworth Sleepiness Scale',
};

export interface PatientDocument {
  id: string;
  title: string;
  description?: string;
  url?: string;
  data?: string;
  contentType?: string;
  date?: string;
  authorDisplay?: string;
  /** false for QR-sourced intake uploads — cannot be edited or deleted */
  editable: boolean;
  /** present only when editable=true */
  documentReference?: DocumentReference;
  sourceResponseId?: string;
  sourceQuestionnaireName?: string;
}

function extractIntakeAttachments(
  items: QuestionnaireResponseItem[] | undefined,
  linkIds: Record<string, string>
): { linkId: string; title: string; url: string; contentType?: string }[] {
  const results: { linkId: string; title: string; url: string; contentType?: string }[] = [];
  for (const item of items ?? []) {
    const title = linkIds[item.linkId ?? ''];
    const url = item.answer?.[0]?.valueAttachment?.url;
    if (title && url) {
      results.push({
        linkId: item.linkId ?? '',
        title,
        url,
        contentType: item.answer?.[0]?.valueAttachment?.contentType,
      });
    }
    results.push(...extractIntakeAttachments(item.item, linkIds));
  }
  return results;
}

export interface UsePatientDocumentsResult {
  documents: PatientDocument[];
  loading: boolean;
  error: Error | null;
  setDocuments: (documents: PatientDocument[]) => void;
  refresh: () => void;
}

export function usePatientDocuments(patient: Patient | undefined): UsePatientDocumentsResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [initialDocRefs, qrs, questionnaires] = await Promise.all([
        medplum.searchResources('DocumentReference', {
          subject: `Patient/${patient.id}`,
          _sort: '-date',
          _count: '200',
        }),
        medplum.searchResources('QuestionnaireResponse', {
          subject: `Patient/${patient.id}`,
          _sort: '-authored',
          _count: '100',
        }),
        Promise.all(
          ASSESSMENT_CATALOG.map(async (entry) => {
            const questionnaire = await medplum
              .searchOne('Questionnaire', { name: entry.questionnaireName })
              .catch(() => undefined);
            return [entry.questionnaireName, questionnaire] as const;
          })
        ),
      ]);

      const docRefs = [...(initialDocRefs as DocumentReference[])];
      let generatedDocument = false;
      const completedResponses = (qrs as QuestionnaireResponse[]).filter(
        (response) => response.status === 'completed' && response.id
      );
      for (const [questionnaireName, questionnaire] of questionnaires as [string, Questionnaire | undefined][]) {
        if (!questionnaire?.id) {
          continue;
        }
        const response = completedResponses
          .filter(
            (candidate) =>
              candidate.questionnaire === `Questionnaire/${questionnaire.id}` ||
              candidate.questionnaire === `Questionnaire/${questionnaireName}`
          )
          .sort((a, b) =>
            (b.authored ?? b.meta?.lastUpdated ?? '').localeCompare(a.authored ?? a.meta?.lastUpdated ?? '')
          )[0];
        if (!response) {
          continue;
        }
        const currentDocument = docRefs.find(
          (doc) =>
            doc.status === 'current' && getQuestionnaireDocumentSource(doc).questionnaireName === questionnaireName
        );
        if (currentDocument && getQuestionnaireDocumentSource(currentDocument).responseId === response.id) {
          continue;
        }
        try {
          const created = await createVersionedQuestionnaireDocument({
            medplum,
            patient,
            practitioner: profile,
            questionnaire,
            response,
            previous: currentDocument,
          });
          docRefs.push(created);
          generatedDocument = true;
        } catch (err) {
          console.error(`Failed to create intake document for ${questionnaireName}`, err);
        }
      }

      if (generatedDocument) {
        docRefs.splice(
          0,
          docRefs.length,
          ...((await medplum.searchResources('DocumentReference', {
            subject: `Patient/${patient.id}`,
            _sort: '-date',
            _count: '200',
          })) as DocumentReference[])
        );
      }

      const fromDocRefs: PatientDocument[] = docRefs
        .filter((doc) => {
          const generated = doc.type?.coding?.some(
            (coding) =>
              coding.system === QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM && coding.code === QUESTIONNAIRE_DOCUMENT_TYPE_CODE
          );
          return doc.status === 'current' || generated;
        })
        .map((doc) => {
          const source = getQuestionnaireDocumentSource(doc);
          return {
            id: doc.id ?? '',
            title: doc.content?.[0]?.attachment?.title ?? doc.description ?? '—',
            description: doc.description,
            url: doc.content?.[0]?.attachment?.url,
            data: doc.content?.[0]?.attachment?.data,
            contentType: doc.content?.[0]?.attachment?.contentType,
            date: doc.date ?? doc.meta?.lastUpdated,
            authorDisplay: doc.author?.[0]?.display,
            editable: source.responseId ? doc.status === 'current' : true,
            documentReference: doc,
            sourceResponseId: source.responseId,
            sourceQuestionnaireName: source.questionnaireName,
          };
        });

      // Deduplicate intake uploads by linkId — keep the one from the most recent QR
      const seenLinkIds = new Set<string>();
      const fromQRs: PatientDocument[] = [];
      for (const qr of Array.from(qrs) as QuestionnaireResponse[]) {
        const attachments = extractIntakeAttachments(qr.item, INTAKE_UPLOAD_LINK_IDS);
        for (const att of attachments) {
          if (seenLinkIds.has(att.linkId)) {
            continue;
          }
          seenLinkIds.add(att.linkId);
          fromQRs.push({
            id: `qr-${qr.id ?? ''}-${att.linkId}`,
            title: att.title,
            description: 'Intake upload',
            url: att.url,
            contentType: att.contentType,
            date: qr.authored ?? qr.meta?.lastUpdated,
            authorDisplay: qr.author?.display,
            editable: false,
          });
        }
      }

      setDocuments([...fromDocRefs, ...fromQRs]);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load documents'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id, patient, profile]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { documents, loading, error, setDocuments, refresh: fetch };
}
